import {
  Box,
  Flex,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { PaginateNavigate } from "components/paginateComponent/PaginateComponent";

// Custom components
import * as React from "react";
import { useHistory } from "react-router-dom";
import { formaterDocument } from "utils/formaterDocumente";
import { useClients } from "hooks/ClientsHook";
import { useQuery } from "react-query";

type RowObj = {
  clientes: string;
  documento: number;
  cidade: string;
  ultimo_acesso: string;
  dias_ultimo_acesso: number;
};

interface FilterClientsProps {
  dias: number;
  nome?: string;
}

const columnHelper = createColumnHelper<RowObj>();

// const columns = columnsDataCheck;
export default function ClientsInactiveTable(parms: FilterClientsProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const [paginate, setPaginate] = React.useState(1);
  const navigate = useHistory();
  const { findInactiveClients } = useClients();
  const { data, error, isLoading } = useQuery(
    ["findInactiveClients", parms, paginate],
    () =>
      findInactiveClients({
        ...parms,
        page: paginate,
      })
  );
  const totalResults = data?.total || 0;
  const navigatePages = {
    preview_url: data?.links || [],
    current_page: data?.current_page || 1,
    last_page: data?.last_page || 1,
  };

  const dataTable: RowObj[] = Array.isArray(data?.data)
    ? data.data.map((item: any) => ({
        clientes: item.NOME,
        documento: item.DOCUMENTO,
        cidade: item.CIDADE,
        ultimo_acesso: item.ULTIMO_ACESSO,
        dias_ultimo_acesso: item.DIAS_ULTIMO_ACESSO,
        id: item.ID,
      }))
    : [];

  const columns = [
    columnHelper.accessor("clientes", {
      id: "clientes",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          color="gray.400"
        >
          NOME
        </Text>
      ),
      cell: (info: any) => (
        <Flex align="center">
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor("documento", {
      id: "documento",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          color="gray.400"
        >
          DOCUMENTO
        </Text>
      ),
      cell: (info) => (
        <Text color={textColorSecondary} fontSize="sm" fontWeight="500">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor("cidade", {
      id: "cidade",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          color="gray.400"
        >
          CIDADE
        </Text>
      ),
      cell: (info: any) => (
        <Flex align="center">
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor("ultimo_acesso", {
      id: "ultimo_acesso",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          color="gray.400"
        >
          ULTIMO ACESSO
        </Text>
      ),
      cell: (info: any) => (
        <Flex align="center">
          <Text
            color={textColor}
            fontSize="sm"
            fontWeight="600"
            textAlign={"left"}
          >
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor("dias_ultimo_acesso", {
      id: "dias_ultimo_acesso",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          color="gray.400"
        >
          DIAS ATRAS
        </Text>
      ),
      cell: (info: any) => (
        <Flex align="center">
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
  ];
  const table = useReactTable({
    data: dataTable,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  return (
    <Flex
      direction="column"
      maxW={{ base: "100vw" }}
      overflowX={{ sm: "scroll", lg: "hidden" }}
    >
      {isLoading ? (
        <Box
          display="flex"
          alignItems="center"
          padding={30}
          justifyContent="center"
        >
          <Spinner width={100} height={100} />
        </Box>
      ) : error ? (
        <Flex w={"100%"} height={"100%"} justify={"center"} align={"center"}>
          <Text color={"red.500"} fontSize={"xl"}>
            {String(error)}
          </Text>
        </Flex>
      ) : (
        <>
          <Box>
            <Flex
              px="25px"
              mb="8px"
              justifyContent="space-between"
              align="center"
            >
              <Text
                color={textColor}
                fontSize="14px"
                fontWeight="700"
                lineHeight="100%"
              >
                TOTAL : {totalResults}
              </Text>
            </Flex>
            <Table variant="simple" color="gray.500" mt="12px">
              <Thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <Th
                          key={header.id}
                          colSpan={header.colSpan}
                          pe="10px"
                          borderColor={borderColor}
                          cursor="pointer"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <Flex
                            justifyContent="space-between"
                            align="center"
                            fontSize={{ sm: "10px", lg: "12px" }}
                            color="gray.400"
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: "",
                              desc: "",
                            }[header.column.getIsSorted() as string] ?? null}
                          </Flex>
                        </Th>
                      );
                    })}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {dataTable.map((data: any, idx: number) => (
                  <Tr
                    key={data.id}
                    color={textColor}
                    cursor="pointer"
                    onClick={() =>
                      navigate.push(`/admin/clientInfo?idClient=${data.id}`)
                    }
                    _hover={{
                      opacity: 0.6,
                    }}
                  >
                    <Td
                      fontSize={{ sm: "13px" }}
                      minW={{ sm: "150px", md: "200px", lg: "auto" }}
                      borderColor="transparent"
                    >
                      {data?.clientes
                        ? String(data.clientes).toUpperCase()
                        : "-"}
                    </Td>
                    <Td
                      fontSize={{ sm: "13px" }}
                      minW={{ sm: "150px", md: "200px", lg: "auto" }}
                      borderColor="transparent"
                    >
                      {data?.documento ? formaterDocument(data.documento) : "-"}
                    </Td>
                    <Td
                      fontSize={{ sm: "13px" }}
                      minW={{ sm: "150px", md: "200px", lg: "auto" }}
                      borderColor="transparent"
                    >
                      {data?.cidade || "-"}
                    </Td>
                    <Td
                      textAlign="left"
                      fontSize={{ sm: "13px" }}
                      minW={{ sm: "150px", md: "200px", lg: "auto" }}
                      borderColor="transparent"
                    >
                      {data?.ultimo_acesso || "-"}
                    </Td>
                    <Td
                      textAlign="center"
                      fontSize={{ sm: "13px" }}
                      minW={{ sm: "150px", md: "200px", lg: "auto" }}
                      borderColor="transparent"
                    >
                      {typeof data?.dias_ultimo_acesso === "number"
                        ? data.dias_ultimo_acesso
                        : "-"}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          <PaginateNavigate
            current_page={navigatePages.current_page}
            pages_preview={navigatePages.preview_url}
            pageSelect={setPaginate}
            lastPage={navigatePages.last_page}
          />
        </>
      )}
    </Flex>
  );
}
